/**
 * ============================================
 * FRIEND CODE DASHBOARD
 * ============================================
 *
 * Shows friend code analytics:
 * - Friend Code Initialized (per platform over time)
 * - Friend Code Redeemed (per platform over time)
 */

import { useState } from 'react';
import { UserPlus, Gift, RefreshCw, Car } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useTheme } from '@/context/ThemeContext';
import { useDateRange } from '@/context/DateRangeContext';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';

// ============================================
// CONSTANTS
// ============================================

const PLATFORM_SERIES = [
  { key: 'pc',  label: 'PC',  color: '#3b82f6' }, // blue
  { key: 'ps5', label: 'PS5', color: '#a855f7' }, // purple
  { key: 'ios', label: 'iOS', color: '#22c55e' }, // green
] as const;

type TimeScale = 'daily' | 'weekly' | 'monthly';

// ============================================
// HELPERS
// ============================================

/** Find the earliest date across all friend code datasets. */
function getGlobalStartDate(
  ...datasets: (Record<string, Record<string, number>> | undefined)[]
): string | null {
  let earliest: string | null = null;
  for (const ds of datasets) {
    if (!ds) continue;
    for (const d of Object.keys(ds)) {
      if (!earliest || d < earliest) earliest = d;
    }
  }
  return earliest;
}

/** Generate all ISO date strings from start to yesterday (inclusive). */
function generateDateRange(startIso: string): string[] {
  const dates: string[] = [];
  const current = new Date(startIso + 'T00:00:00');
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  while (current <= yesterday) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function buildChartData(
  raw: Record<string, Record<string, number>> | undefined,
  timeScale: TimeScale,
  allDates: string[],
) {
  if (!raw || allDates.length === 0) return [];

  if (timeScale === 'daily') {
    return allDates.map(d => {
      const day = raw[d] || {};
      const dt = new Date(d + 'T00:00:00');
      return {
        date: `${dt.getDate().toString().padStart(2, '0')}-${dt.toLocaleDateString('en-US', { month: 'short' })}`,
        pc:  day['pc'] || day['windows'] || 0,
        ps5: day['ps5'] || day['playstation'] || 0,
        ios: day['ios'] || day['iphone'] || 0,
      };
    });
  }

  // Weekly / Monthly aggregation
  const groups: Record<string, { label: string; pc: number; ps5: number; ios: number }> = {};
  allDates.forEach(d => {
    const dt = new Date(d + 'T00:00:00');
    const day = raw[d] || {};
    let key: string, label: string;
    if (timeScale === 'weekly') {
      const ws = new Date(dt);
      ws.setDate(dt.getDate() - dt.getDay());
      key = ws.toISOString().split('T')[0];
      label = `${ws.getDate().toString().padStart(2, '0')}-${ws.toLocaleDateString('en-US', { month: 'short' })}`;
    } else {
      key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      label = dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    if (!groups[key]) groups[key] = { label, pc: 0, ps5: 0, ios: 0 };
    groups[key].pc  += day['pc'] || day['windows'] || 0;
    groups[key].ps5 += day['ps5'] || day['playstation'] || 0;
    groups[key].ios += day['ios'] || day['iphone'] || 0;
  });

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => ({ date: g.label, pc: g.pc, ps5: g.ps5, ios: g.ios }));
}

// ============================================
// THEMED CARD
// ============================================

function ThemedCard({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200 shadow-md'}`}>
      {children}
    </div>
  );
}

// ============================================
// FRIEND CODE CHART COMPONENT
// ============================================

function FriendCodeChart({
  title,
  icon,
  raw,
  allDates,
  timeScale,
  setTimeScale,
  isDarkMode,
  gridColor,
  textColor,
  tooltipStyle,
}: {
  title: string;
  icon: React.ReactNode;
  raw: Record<string, Record<string, number>> | undefined;
  allDates: string[];
  timeScale: TimeScale;
  setTimeScale: (s: TimeScale) => void;
  isDarkMode: boolean;
  gridColor: string;
  textColor: string;
  tooltipStyle: any;
}) {
  const chartData = buildChartData(raw, timeScale, allDates);

  return (
    <ThemedCard isDark={isDarkMode}>
      <div className="p-4 border-b border-gray-700/50 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          {icon}
          {title}
        </h3>
        <div className="flex gap-1">
          {(['daily', 'weekly', 'monthly'] as const).map((s) => (
            <button key={s} onClick={() => setTimeScale(s)}
              className={`px-2 py-1 text-xs rounded ${timeScale === s
                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
              }`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
          ))}
        </div>
      </div>
      <div className="p-4 h-[350px]">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Awaiting next analytics sync for friend code data...
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="date"
                tick={({ x, y, payload }: any) => (
                  <text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9}
                    transform={`rotate(-35, ${x}, ${y})`}>{String(payload.value).toUpperCase()}</text>
                )}
                stroke={gridColor}
                height={60}
              />
              <YAxis
                tick={{ fontSize: 10, fill: textColor }}
                stroke={gridColor}
                allowDecimals={false}
                label={{ value: 'Users', angle: -90, position: 'insideLeft', fill: textColor, fontSize: 11 }}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              {PLATFORM_SERIES.map(p => (
                <Line key={p.key} type="monotone" dataKey={p.key} name={p.label}
                  stroke={p.color} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </ThemedCard>
  );
}

// ============================================
// MAIN DASHBOARD
// ============================================

export function FriendCodeDashboard() {
  const { data, isLoading, isFetching, refetch } = useAnalytics();
  const { isDarkMode } = useTheme();
  const { inRange } = useDateRange();

  const [initTimeScale, setInitTimeScale] = useState<TimeScale>('daily');
  const [redeemTimeScale, setRedeemTimeScale] = useState<TimeScale>('daily');
  const [carTrackTimeScale, setCarTrackTimeScale] = useState<TimeScale>('daily');

  const gridColor = isDarkMode ? '#374151' : '#e5e7eb';
  const textColor = isDarkMode ? '#9CA3AF' : '#6B7280';
  const tooltipStyle = isDarkMode
    ? { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }
    : { backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' };

  // Compute a shared date range across all 3 datasets so charts are aligned
  const globalStart = getGlobalStartDate(
    data?.dailyFriendCodeInitialized,
    data?.dailyFriendCodeRedeemed,
    data?.dailyCarTrackEditorUnlocked,
  );
  // Full span from the data, then narrowed to the shared date-range filter.
  const allDates = (globalStart ? generateDateRange(globalStart) : []).filter(inRange);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Friend Code Analytics
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
        <DateRangeFilter />
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
            isDarkMode
              ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        </div>
      </div>

      {/* Friend Code Initialized Chart */}
      <FriendCodeChart
        title="Friend Code Initialized"
        icon={<UserPlus className="h-4 w-4" />}
        raw={data?.dailyFriendCodeInitialized}
        allDates={allDates}
        timeScale={initTimeScale}
        setTimeScale={setInitTimeScale}
        isDarkMode={isDarkMode}
        gridColor={gridColor}
        textColor={textColor}
        tooltipStyle={tooltipStyle}
      />

      {/* Friend Code Redeemed Chart */}
      <FriendCodeChart
        title="Friend Code Redeemed"
        icon={<Gift className="h-4 w-4" />}
        raw={data?.dailyFriendCodeRedeemed}
        allDates={allDates}
        timeScale={redeemTimeScale}
        setTimeScale={setRedeemTimeScale}
        isDarkMode={isDarkMode}
        gridColor={gridColor}
        textColor={textColor}
        tooltipStyle={tooltipStyle}
      />

      {/* Car Track Editor Unlocked Chart */}
      <FriendCodeChart
        title="Car Track Editor Unlocked"
        icon={<Car className="h-4 w-4" />}
        raw={data?.dailyCarTrackEditorUnlocked}
        allDates={allDates}
        timeScale={carTrackTimeScale}
        setTimeScale={setCarTrackTimeScale}
        isDarkMode={isDarkMode}
        gridColor={gridColor}
        textColor={textColor}
        tooltipStyle={tooltipStyle}
      />
    </div>
  );
}
